import i18next from "i18next";

export type TVehicleType = "New" | "Pre-owned";

export type TBodyType =
  | "sedan"
  | "hatchback"
  | "suv"
  | "crossover"
  | "off_road_suv"
  | "pickup_truck"
  | "coupe"
  | "convertible"
  | "wagon"
  | "minivan"
  | "passenger_van"
  | "cargo_van"
  | "roadster"
  | "van"
  | "chassis_cab";

export type TEngineType = "gasoline" | "diesel" | "electric" | "hybrid";

export type TTransmissionType =
  | "manual"
  | "automatic"
  | "cvt"
  | "dct"
  | "amt"
  | "ev_single"
  | "other";

export type TTransmissionSpeed = "5" | "6" | "7" | "8" | "9" | "10" | "11+";

export type TTimeUnit = "minutes" | "hours";

export interface IOptionItem<T = string> {
  value: T;
  label: string;
  icon?: string
}
export interface IMake {
  id: number;
  name: string;
  icon?: string;
}
export interface IModel {
  id: number;
  name: string;
}

export const BODY_TYPE_OPTIONS: IOptionItem<TBodyType>[] = [
  { value: "sedan", get label() { return i18next.t?.("dealershipOptions.sedan") || "Sedan"; } },
  { value: "hatchback", get label() { return i18next.t?.("dealershipOptions.hatchback") || "Hatchback"; } },
  { value: "suv", get label() { return i18next.t?.("dealershipOptions.suv") || "SUV"; } },
  { value: "crossover", get label() { return i18next.t?.("dealershipOptions.crossover") || "Crossover"; } },
  { value: "off_road_suv", get label() { return i18next.t?.("dealershipOptions.off_road_suv") || "Off-road SUV"; } },
  { value: "pickup_truck", get label() { return i18next.t?.("dealershipOptions.pickup_truck") || "Pickup Truck"; } },
  { value: "coupe", get label() { return i18next.t?.("dealershipOptions.coupe") || "Coupe"; } },
  { value: "convertible", get label() { return i18next.t?.("dealershipOptions.convertible") || "Convertible"; } },
  { value: "wagon", get label() { return i18next.t?.("dealershipOptions.wagon") || "Wagon / Estate"; } },
  { value: "minivan", get label() { return i18next.t?.("dealershipOptions.minivan") || "Minivan"; } },
  { value: "passenger_van", get label() { return i18next.t?.("dealershipOptions.passenger_van") || "Passenger Van"; } },
  { value: "cargo_van", get label() { return i18next.t?.("dealershipOptions.cargo_van") || "Cargo Van"; } },
  { value: "roadster", get label() { return i18next.t?.("dealershipOptions.roadster") || "Roadster"; } },
];

export const ENGINE_OPTIONS: IOptionItem<TEngineType>[] = [
  { value: "gasoline", get label() { return i18next.t?.("dealershipOptions.gasoline") || "Gasoline"; } },
  { value: "diesel", get label() { return i18next.t?.("dealershipOptions.diesel") || "Diesel"; } },
  { value: "electric", get label() { return i18next.t?.("dealershipOptions.electric") || "Electric"; } },
  { value: "hybrid", get label() { return i18next.t?.("dealershipOptions.hybrid") || "Hybrid"; } },
];

export const TRANSMISSION_TYPE_OPTIONS: IOptionItem<TTransmissionType>[] = [
  { value: "automatic", get label() { return i18next.t?.("dealershipOptions.automatic") || "Automatic"; } },
  { value: "manual", get label() { return i18next.t?.("dealershipOptions.manual") || "Manual"; } },
  { value: "cvt", get label() { return i18next.t?.("dealershipOptions.cvt") || "CVT (Continuously Variable)"; } },
  { value: "dct", get label() { return i18next.t?.("dealershipOptions.dct") || "Dual-Clutch (DCT)"; } },
  { value: "amt", get label() { return i18next.t?.("dealershipOptions.amt") || "Automated Manual (AMT)"; } },
  { value: "ev_single", get label() { return i18next.t?.("dealershipOptions.ev_single") || "EV Single-Speed"; } },
  { value: "other", get label() { return i18next.t?.("dealershipOptions.other") || "Other / Unknown"; } },
];

export const TRANSMISSION_SPEED_OPTIONS: IOptionItem<TTransmissionSpeed>[] = [
  { value: "5", get label() { return i18next.t?.("dealershipOptions.speed5") || "5 Speeds"; } },
  { value: "6", get label() { return i18next.t?.("dealershipOptions.speed6") || "6 Speeds"; } },
  { value: "7", get label() { return i18next.t?.("dealershipOptions.speed7") || "7 Speeds"; } },
  { value: "8", get label() { return i18next.t?.("dealershipOptions.speed8") || "8 Speeds"; } },
  { value: "9", get label() { return i18next.t?.("dealershipOptions.speed9") || "9 Speeds"; } },
  { value: "10", get label() { return i18next.t?.("dealershipOptions.speed10") || "10 Speeds"; } },
  { value: "11+", get label() { return i18next.t?.("dealershipOptions.speed11Plus") || "11+ Speeds"; } },
];

const CURRENT_YEAR = new Date().getFullYear();
export const YEAR_OPTIONS: IOptionItem<string>[] = Array.from(
  { length: CURRENT_YEAR + 2 - 1970 },
  (_, index) => {
    const y = String(CURRENT_YEAR + 1 - index);
    return { value: y, label: y };
  },
);

export const CYLINDER_OPTIONS: IOptionItem<string>[] = [
  { value: "3", get label() { return i18next.t?.("dealershipOptions.cylinders3") || "3 Cylinders"; } },
  { value: "4", get label() { return i18next.t?.("dealershipOptions.cylinders4") || "4 Cylinders"; } },
  { value: "6", get label() { return i18next.t?.("dealershipOptions.cylinders6") || "6 Cylinders"; } },
  { value: "8", get label() { return i18next.t?.("dealershipOptions.cylinders8") || "8 Cylinders"; } },
  { value: "12", get label() { return i18next.t?.("dealershipOptions.cylinders12") || "12 Cylinders"; } },
  { value: "16", get label() { return i18next.t?.("dealershipOptions.cylinders16") || "16 Cylinders"; } },
];

export interface IFeatureItem {
  key: string;
  label: string;
}

export interface IFeatureCategory {
  id: string;
  title: string;
  items: IFeatureItem[];
}

export const VEHICLE_FEATURE_CATEGORIES: IFeatureCategory[] = [
  {
    id: "exterior",
    title: "Exterior & Lighting",
    items: [
      {
        key: "led_drl_with_integrated_turn_signal",
        label: "LED DRL with integrated turn signal",
      },
      {
        key: "grille_with_chrome_and_gloss_black_accents",
        label: "Grille with chrome & gloss black accents",
      },
      {
        key: "fifteen_inch_two_tone_aluminum_wheels",
        label: "15-inch two-tone aluminum wheels",
      },
      {
        key: "halogen_projector_headlights",
        label: "Halogen projector headlights",
      },
      {
        key: "spoiler_with_third_brake_light",
        label: "Spoiler with third brake light",
      },
      {
        key: "gloss_black_shark_fin_antenna",
        label: "Gloss black shark fin antenna",
      },
      {
        key: "panoramic_sunroof",
        label: "Panoramic sunroof",
      },
      {
        key: "roof_rails",
        label: "Roof rails",
      },
      {
        key: "rear_fog_lights",
        label: "Rear fog lights",
      },
    ],
  },
  {
    id: "interior",
    title: "Interior & Comfort",
    items: [
      {
        key: "folding_rear_seats_60_40_split",
        label: "Folding rear seats 60/40 split",
      },
      {
        key: "spacious_trunk_cargo_area",
        label: "Spacious trunk / cargo area",
      },
      {
        key: "power_windows_with_remote_opening",
        label: "Power windows with remote opening",
      },
      {
        key: "black_cloth_seats_with_gray_trim",
        label: "Black cloth seats with gray trim",
      },
      {
        key: "electronic_ac_with_automatic_controls",
        label: "Electronic AC with automatic controls",
      },
      {
        key: "three_point_five_inch_monochrome_instrument_cluster",
        label: "3.5-inch monochrome instrument cluster",
      },
      {
        key: "steering_wheel_with_audio_controls",
        label: "Steering wheel with audio controls",
      },
      {
        key: "six_way_power_drivers_seat",
        label: "6-way power driver's seat",
      },
      {
        key: "electric_parking_brake_with_center_console_button",
        label: "Electric parking brake with button",
      },
      {
        key: "second_row_60_40_folding_seats",
        label: "Second-row 60/40 folding seats",
      },
      {
        key: "third_row_50_50_folding_seats",
        label: "Third-row 50/50 folding seats",
      },
      {
        key: "flexible_configuration_to_maximize_interior_space",
        label: "Flexible configuration for space",
      },
      {
        key: "two_rows_of_seating_for_five_passengers",
        label: "Two rows seating for 5 passengers",
      },
      {
        key: "six_way_adjustable_drivers_seat",
        label: "6-way adjustable driver's seat",
      },
      {
        key: "retractable_key",
        label: "Retractable key",
      },
      {
        key: "front_and_rear_ac_with_manual_controls",
        label: "Front & rear AC with manual controls",
      },
      {
        key: "three_spoke_steering_wheel_with_audio_controls",
        label: "3-spoke steering wheel with audio controls",
      },
    ],
  },
  {
    id: "infotainment",
    title: "Infotainment & Audio",
    items: [
      {
        key: "eight_inch_touchscreen_with_android_auto_apple_carplay",
        label: "8-inch touchscreen with CarPlay & Android Auto",
      },
      {
        key: "audio_system_with_four_speakers",
        label: "Audio system with 4 speakers",
      },
      {
        key: "bluetooth_and_am_fm_radio",
        label: "Bluetooth & AM/FM radio",
      },
      {
        key: "two_front_usb_ports",
        label: "Two front USB ports",
      },
      {
        key: "radio_with_mp3_and_bluetooth",
        label: "Radio with MP3 & Bluetooth",
      },
    ],
  },
  {
    id: "safety",
    title: "Safety & Handling",
    items: [
      {
        key: "six_airbags_front_side_curtain",
        label: "6 airbags (front, side, curtain)",
      },
      {
        key: "reverse_sensors",
        label: "Reverse parking sensors",
      },
      {
        key: "seatbelt_alert_in_all_positions",
        label: "Seatbelt alert in all positions",
      },
      {
        key: "electric_power_steering",
        label: "Electric power steering",
      },
      {
        key: "mcpherson_front_suspension",
        label: "MacPherson front suspension",
      },
      {
        key: "trailer_stability_assist",
        label: "Trailer stability assist",
      },
      {
        key: "stabilitrak_electronic_stability_control",
        label: "StabiliTrak stability control",
      },
      {
        key: "autonomous_emergency_braking",
        label: "Autonomous emergency braking",
      },
      {
        key: "isofix_child_seat_anchors",
        label: "ISOFIX child seat anchors",
      },
      {
        key: "three_point_seatbelts_in_all_positions",
        label: "3-point seatbelts in all positions",
      },
      {
        key: "seatbelt_usage_indicators",
        label: "Seatbelt usage indicators",
      },
      {
        key: "anti_theft_alarm",
        label: "Anti-theft alarm",
      },
      {
        key: "three_sixty_degree_vision_camera",
        label: "360-degree vision camera",
      },
      {
        key: "front_sensors",
        label: "Front parking sensors",
      },
      {
        key: "tire_pressure_monitoring_system_tpms",
        label: "Tire pressure monitoring system (TPMS)",
      },
      {
        key: "cruise_control",
        label: "Cruise control",
      },
    ],
  },
  {
    id: "capacity",
    title: "Capacity & Performance",
    items: [
      {
        key: "powerful_and_spacious_configuration",
        label: "Powerful & spacious configuration",
      },
      {
        key: "four_by_two_drivetrain",
        label: "4x2 drivetrain",
      },
      {
        key: "truck_bed",
        label: "Truck bed",
      },
      {
        key: "payload_capacity_up_to_1000_kg",
        label: "Payload capacity up to 1,000 kg",
      },
      {
        key: "magnetic_ride_control_suspension",
        label: "Magnetic ride control suspension",
      },
    ],
  },
  {
    id: "rst",
    title: "RST / Premium Equipment",
    items: [
      {
        key: "six_point_two_liters_ecotec_v8_engine_425_hp",
        label: "6.2L EcoTec V8 engine (425 hp)",
      },
      {
        key: "dynamic_fuel_management",
        label: "Dynamic fuel management",
      },
      {
        key: "four_sixty_lb_ft_of_torque",
        label: "460 lb-ft of torque",
      },
      {
        key: "retractable_side_steps",
        label: "Retractable power side steps",
      },
      {
        key: "hands_free_tailgate",
        label: "Hands-free power tailgate",
      },
      {
        key: "chrome_roof_rails",
        label: "Chrome roof rails",
      },
      {
        key: "power_tailgate_with_proximity_sensor",
        label: "Power tailgate with proximity sensor",
      },
      {
        key: "power_tilt_and_telescoping_steering_wheel",
        label: "Power tilt & telescoping steering wheel",
      },
      {
        key: "four_way_power_lumbar_front_seats",
        label: "4-way power lumbar front seats",
      },
      {
        key: "sliding_center_console",
        label: "Sliding center console",
      },
      {
        key: "electrochromic_rearview_mirror_with_secondary_camera",
        label: "Electrochromic mirror with camera",
      },
      {
        key: "fifteen_inch_head_up_display",
        label: "15-inch Head-Up Display (HUD)",
      },
      {
        key: "vehicle_inclination_sensor",
        label: "Vehicle inclination sensor",
      },
      {
        key: "forced_locking_system",
        label: "Forced locking system",
      },
    ],
  },
];

export interface IVehicleFormData {
  type: TVehicleType;
  number_of_owners: string;
  number_of_accidents: string;
  body_type: TBodyType;

  make: string;
  model: string;
  year: string;
  mileage: string;
  article_code: string;
  serial_number: string;
  description: string;
  notes: string;

  price: string;
  leasing_price: string;
  is_demo: boolean;

  engine: TEngineType;
  engine_origin: string;
  motor_volume: string;
  motor_cylinders: string;
  horse_power: string;
  torque: string;
  transmission_type: TTransmissionType;
  transmission_speed: TTransmissionSpeed;
  warranty: string;

  exterior_color: string;
  interior_color: string;
  interior_type: string;

  viewing_duration: string;
  viewing_duration_time_unit: TTimeUnit;
  buffer_time: string;
  buffer_time_time_unit: TTimeUnit;

  images: any[];
  technical_document: any | null;

  features: Record<string, boolean>;
}

export interface IVehicleDetailsResponse {
  id: number;
  make_name: string;
  model_name: string;
  images?: unknown[];
  exterior_images?: unknown[];
  interior_images?: unknown[];
  type: string;
  number_of_owners: string | null;
  number_of_accidents: string | null;
  body_type: string;
  article_code: string | null;
  description: string;
  serial_number: string | null;
  engine_origin: null;
  year: number;
  mileage: number;
  exterior_color: string;
  interior_color: string;
  interior_type: string | null;
  price: string;
  leasing_price: string | null;
  is_demo: boolean;
  engine: string;
  motor_volume: string | null;
  motor_cylinders: string | null;
  horse_power: string | null;
  torque: string | null;
  transmission_type: string;
  transmission_speed: string;
  warranty: string;
  notes: string | null;
  technical_document: string | null;
  led_drl_with_integrated_turn_signal: boolean;
  grille_with_chrome_and_gloss_black_accents: boolean;
  fifteen_inch_two_tone_aluminum_wheels: boolean;
  halogen_projector_headlights: boolean;
  spoiler_with_third_brake_light: boolean;
  gloss_black_shark_fin_antenna: boolean;
  folding_rear_seats_60_40_split: boolean;
  spacious_trunk_cargo_area: boolean;
  power_windows_with_remote_opening: boolean;
  black_cloth_seats_with_gray_trim: boolean;
  electronic_ac_with_automatic_controls: boolean;
  three_point_five_inch_monochrome_instrument_cluster: boolean;
  steering_wheel_with_audio_controls: boolean;
  six_way_power_drivers_seat: boolean;
  electric_parking_brake_with_center_console_button: boolean;
  second_row_60_40_folding_seats: boolean;
  third_row_50_50_folding_seats: boolean;
  flexible_configuration_to_maximize_interior_space: boolean;
  two_rows_of_seating_for_five_passengers: boolean;
  six_way_adjustable_drivers_seat: boolean;
  retractable_key: boolean;
  front_and_rear_ac_with_manual_controls: boolean;
  three_spoke_steering_wheel_with_audio_controls: boolean;
  eight_inch_touchscreen_with_android_auto_apple_carplay: boolean;
  audio_system_with_four_speakers: boolean;
  bluetooth_and_am_fm_radio: boolean;
  two_front_usb_ports: boolean;
  radio_with_mp3_and_bluetooth: boolean;
  six_airbags_front_side_curtain: boolean;
  six_airbags_two_front_two_side_two_curtain: boolean;
  reverse_sensors: boolean;
  seatbelt_alert_in_all_positions: boolean;
  electric_power_steering: boolean;
  mcpherson_front_suspension: boolean;
  trailer_stability_assist: boolean;
  stabilitrak_electronic_stability_control: boolean;
  autonomous_emergency_braking: boolean;
  panoramic_sunroof: boolean;
  roof_rails: boolean;
  isofix_child_seat_anchors: boolean;
  three_point_seatbelts_in_all_positions: boolean;
  seatbelt_usage_indicators: boolean;
  anti_theft_alarm: boolean;
  three_sixty_degree_vision_camera: boolean;
  front_sensors: boolean;
  tire_pressure_monitoring_system_tpms: boolean;
  cruise_control: boolean;
  powerful_and_spacious_configuration: boolean;
  four_by_two_drivetrain: boolean;
  truck_bed: boolean;
  payload_capacity_up_to_1000_kg: boolean;
  rear_fog_lights: boolean;
  magnetic_ride_control_suspension: boolean;
  six_point_two_liters_ecotec_v8_engine_425_hp: boolean;
  dynamic_fuel_management: boolean;
  four_sixty_lb_ft_of_torque: boolean;
  retractable_side_steps: boolean;
  hands_free_tailgate: boolean;
  chrome_roof_rails: boolean;
  power_tailgate_with_proximity_sensor: boolean;
  power_tilt_and_telescoping_steering_wheel: boolean;
  four_way_power_lumbar_front_seats: boolean;
  sliding_center_console: boolean;
  electrochromic_rearview_mirror_with_secondary_camera: boolean;
  fifteen_inch_head_up_display: boolean;
  vehicle_inclination_sensor: boolean;
  forced_locking_system: boolean;
  viewing_duration: number;
  viewing_duration_time_unit: string;
  buffer_time: number;
  buffer_time_time_unit: string;
  created_at: string;
  make: number;
  model: number;
}

export const EXTERIOR_IMAGE_TYPE_OPTIONS: IOptionItem<string>[] = [
  { label: "Front", value: "front" },
  { label: "Side", value: "side" },
  { label: "Rear", value: "rear" },
  { label: "Top-Down", value: "top_down" },
  { label: "Hood", value: "hood" },
  { label: "Roof", value: "roof" },
  { label: "Windshield", value: "windshield" },
  { label: "Side Window", value: "side_window" },
  { label: "Lights", value: "lights" },
  { label: "Wheel Rim", value: "wheel_rim" },
  { label: "Tire", value: "tire" },
  { label: "Brakes", value: "brakes" },
  { label: "Exhaust", value: "exhaust" },
];

export const INTERIOR_IMAGE_TYPE_OPTIONS: IOptionItem<string>[] = [
  { label: "Engine", value: "engine" },
  { label: "Front Seats", value: "front_seats" },
  { label: "Rear Seats", value: "rear_seats" },
  { label: "Cockpit", value: "cockpit" },
  { label: "Driver Seat", value: "driver_seat" },
  { label: "Passenger Seat", value: "passenger_seat" },
  { label: "Steering Wheel", value: "steering_wheel" },
  { label: "Instrument Cluster", value: "instrument_cluster" },
  { label: "Console", value: "console" },
  { label: "Interior Door", value: "interior_door" },
  { label: "Trunk", value: "trunk" },
  { label: "Gear Shifter", value: "gear_shifter" },
  { label: "Air Vent", value: "air_vent" },
  { label: "Glove Box", value: "glove_box" },
  { label: "Seatbelt", value: "seatbelt" },
  { label: "Rooftop", value: "rooftop" },
  { label: "Mirror", value: "mirror" },
  { label: "Pedals", value: "pedals" },
];

export const VEHICLE_IMAGE_TYPE_OPTIONS: IOptionItem<string>[] = [
  ...EXTERIOR_IMAGE_TYPE_OPTIONS,
  ...INTERIOR_IMAGE_TYPE_OPTIONS,
];
